import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { UserData, useAuth } from '@hooks';
import { getUsers } from '@hooks/useUsers/useUsers';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { Button, Table, TableColumnsType, TablePaginationConfig, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { DeleteUserModal } from './DeleteUserModal';
import { UserFormModal } from './UserFormModal';
import './UsersPage.css';

const { Title, Paragraph } = Typography;


export const UsersPage = () => {
  const { profile: { data: currentUser } } = useAuth();
  const [userFormModalOpen, setUserFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  // Pagination and search state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>('descend');

  const params = { 
    page, 
    limit, 
    search, 
    orderBy: sortField, 
    orderType: sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined 
  };
  const { data, isLoading, refetch } = getUsers(params);

  useEffect(() => {
    const timeoutId = setTimeout(() => refetch(), 300);
    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, refetch]);

  const users = data?.data || [];
  const total = data?.pagination?.totalCount || 0;

  const handleTableChange = (_pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    if (Array.isArray(sorter)) return;

    const newField = sorter?.field as string | undefined;
    const newOrder = sorter?.order === 'ascend' ? 'ascend' : sorter?.order === 'descend' ? 'descend' : undefined;

    if (newField !== sortField || newOrder !== sortOrder) {
      setSortField(newField);
      setSortOrder(newOrder);
      setPage(1);
    }
  };

  const columns: TableColumnsType<UserData> = [
    {
      title: <span className="font-azonix">USER</span>,
      key: 'user',
      dataIndex: 'physicalPerson',
      render: (_: any, record: UserData) => (
        <div>
          <div className="font-coda text-white">{record.physicalPerson?.name || record.email}</div>
          <div className="font-coda text-secondary text-sm">{record.email}</div>
        </div>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">PROFILE</span>,
      key: 'profile',
      dataIndex: 'profile',
      render: (profile: string) => (
        <span className="font-coda capitalize">{profile || 'user'}</span>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">CREATED AT</span>,
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (date: string) => (
        <span className="font-coda" style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
          {date ? dayjs(date).format('MM/DD/YYYY') : '-'}
        </span>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">ACTIONS</span>,
      key: 'actions',
      width: 150,
      render: (_: any, record: UserData) => (
        <div className="flex gap-2">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<PencilSimple size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(record);
                setUserFormModalOpen(true);
              }}
              className="text-primary hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#00ade6]"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<Trash size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(record);
                setDeleteModalOpen(true);
              }}
              disabled={record.id === currentUser?.id}
              className="!text-red-500 hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#ef4444]"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="estabrook-bg pt-25 pb-10 md:pb-20">
      <section className="w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <Title level={1} className="font-azonix !font-normal text-primary text-center tracking-wider pb-4 !mb-0"
          >USERS
          </Title>
          <div className="max-w-3xl mx-auto !mb-8">
            <Paragraph className="text-center text-lg text-light">
              Manage all users in the system
            </Paragraph>
          </div>
        </div>
      </section>
      
      <section className="w-full py-12 bg-charcoal">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-3">
            <Title level={2} className="font-azonix !font-normal text-white">USERS LIST</Title>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              className="btn-single-cut"
              onClick={() => { setSelectedUser(null); setUserFormModalOpen(true); }}
            >
              ADD USER
            </Button>
          </div>
          <Paragraph className="text-white !mb-8">
            View, edit or remove users from the system
          </Paragraph>

          {/* Search Input */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              {/* Custom Search Input */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SearchOutlined className="!text-[#00ade6]" style={{ color: '#00ade6' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="w-full sm:w-72 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-[#00ade6] focus:ring-1 focus:ring-[#00ade6]/50 transition-all duration-200"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); setPage(1); }}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-zinc-900/50 border border-zinc-800 shadow-xl overflow-x-auto hide-scrollbar">
            <Table<UserData>
              columns={columns}
              dataSource={users}
              rowKey="id"
              loading={isLoading}
              onRow={(record) => ({ onClick: () => { setSelectedUser(record); setUserFormModalOpen(true); } })}
              onChange={handleTableChange}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange: (currentPage, pageSize) => {
                  setPage(currentPage);
                  setLimit(pageSize);
                },
                showSizeChanger: true,
                showTotal: (total) => <span className="text-white font-coda">Total <span className="text-white font-coda">{total}</span> users</span>,
                className: "!bg-transparent [&_.ant-pagination-item]:!bg-zinc-800 [&_.ant-pagination-item]:!border-zinc-700 [&_.ant-pagination-item-active]:!bg-transparent [&_.ant-pagination-item-active]:!border-zinc-700 [&_.ant-pagination-item_a]:!text-white [&_.ant-pagination-item-active_a]:!text-white [&_.ant-pagination-prev_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-next_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-prev_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-next_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-prev_.ant-pagination-item-link]:!text-white [&_.ant-pagination-next_.ant-pagination-item-link]:!text-white",
              }}
              className="[&_.ant-table]:!bg-transparent [&_.ant-table-thead_th]:!bg-zinc-800/50 [&_.ant-table-thead_th]:!text-white [&_.ant-table-thead_th]:!border-b-[#00ade6] [&_.ant-table-thead_th]:!font-azonix [&_.ant-table-tbody_td]:!bg-transparent [&_.ant-table-tbody_td]:!border-b-zinc-700 [&_.ant-table-tbody_td]:!text-white [&_.ant-table-row:hover_td]:!bg-zinc-800/50 [&_.ant-table-placeholder]:!bg-transparent [&_.ant-empty-description]:!text-white min-w-[800px]"
            />
          </div>
        </div>
      </section>

      <UserFormModal
        open={userFormModalOpen}
        onCancel={() => setUserFormModalOpen(false)}
        editingUser={selectedUser}
      />
      <DeleteUserModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        user={selectedUser}
        onSuccess={refetch}
      />
    </div>
  );
};
